import { datatypes, inputTypes } from "data/globalData";
import {
	BackgroundChangeByBehaviour,
	defRenderCell,
	defRenderCellWithSX,
	defRenderHeader,
	defRenderHeaderWithSX,
	displayInCrRenderCell,
	PercentBackgroundColor,
	StandardRenderCellWithChildren,
	tableBorderStyle,
} from "../../data";
import { grey } from "@mui/material/colors";
import { BorderBottom } from "@mui/icons-material";
import { convertValueInCr, roundDecimalValueUptoTwo } from "utils/roundDecimalValue";
import {
	calculateNsvAchVsAop,
	calculateNsvAchWithProjectionVsAop,
	calculateNsvLost,
	calculateNsvWithProjection,
} from "utils/calcultateAOPDataColumns";

export const nsvDataColumns = {
	nsv_aop: { name: "NSV AOP", key: "nsv_aop" },
	nsv_ach: { name: "NSV ACH", key: "nsv_ach" },
};

const renderCellStyle = {
	boxSizing: "border-box",
	borderBottom: tableBorderStyle,
	borderRight: tableBorderStyle,
};

export const nsvTableColumns = {
	nsv_aop: {
		name: "NSV AOP",
		key: "nsv_aop",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeader,
		headerSx: { background: grey[300] },
		valueGetter: function (row, rawData = false) {
			if (rawData) return row["NSV_AOP_VAL"];
			return convertValueInCr(row["NSV_AOP_VAL"]);
		},
		renderCell: function ({ row, field }) {
			return (
				<StandardRenderCellWithChildren field={field} row={row} sx={renderCellStyle}>
					{this.valueGetter(row)}
				</StandardRenderCellWithChildren>
			);
		},
		headerClassName: "default-header",
		unit: "INR CR.",
	},
	nsv_achieved: {
		name: "NSV ACH",
		key: "nsv_achieved",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeader,
		headerSx: { background: grey[300] },

		valueGetter: function (row, rawData = false) {
			if (rawData) return row["NSV_ACH_VAL"];
			return convertValueInCr(row["NSV_ACH_VAL"]);
		},
		renderCell: function ({ row, field }) {
			return (
				<StandardRenderCellWithChildren field={field} row={row} sx={renderCellStyle}>
					{this.valueGetter(row)}
				</StandardRenderCellWithChildren>
			);
		},
		headerClassName: "default-header",
		unit: "INR CR.",
	},
	nsv_aop_vs_ach: {
		name: "ACH VS AOP",
		key: "nsv_aop_vs_ach",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeaderWithSX({ borderRight: `2px solid ${grey[600]}`, boxSizing: "border-box", p: "2px" }),
		headerSx: { background: grey[300] },

		valueGetter: function (row, rawData = false) {
			if (rawData) return calculateNsvAchVsAop(row);
			return roundDecimalValueUptoTwo(calculateNsvAchVsAop(row));
		},
		renderCell: function ({ row, field }) {
			let val = this.valueGetter(row);
			return (
				<StandardRenderCellWithChildren
					field={field}
					row={row}
					sx={{ ...renderCellStyle, borderRight: `2px solid ${grey[600]}` }}>
					<PercentBackgroundColor value={val}>{val}</PercentBackgroundColor>
				</StandardRenderCellWithChildren>
			);
		},

		headerClassName: "default-header",
		unit: "%",
	},
	nsv_with_projection: {
		name: "NSV with Proj",
		key: "nsv_with_projection",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeader,
		headerSx: { background: grey[300] },

		valueGetter: function (row, rawData = false) {
			if (rawData) return calculateNsvWithProjection(row);
			return convertValueInCr(calculateNsvWithProjection(row));
		},
		renderCell: function ({ row, field }) {
			let val = this.valueGetter(row);
			return (
				<StandardRenderCellWithChildren field={field} row={row} sx={renderCellStyle}>
					{val}
				</StandardRenderCellWithChildren>
			);
		},
		headerClassName: "default-header",
		unit: "INR CR.",
	},
	nsv_proj_ach_vs_aop: {
		name: "ACH VS AOP",
		key: "nsv_proj_ach_vs_aop",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeader,
		headerSx: { background: grey[300] },

		valueGetter: function (row, rawData = false) {
			if (rawData) return calculateNsvAchWithProjectionVsAop(row);
			return roundDecimalValueUptoTwo(calculateNsvAchWithProjectionVsAop(row));
		},
		renderCell: function ({ row, field }) {
			let val = this.valueGetter(row);

			return (
				<StandardRenderCellWithChildren field={field} row={row} sx={renderCellStyle}>
					<PercentBackgroundColor value={val}>{val}</PercentBackgroundColor>
				</StandardRenderCellWithChildren>
			);
		},
		headerClassName: "default-header",
		unit: "%",
	},
	nsv_lost: {
		name: "NSV LOST",
		key: "nsv_lost",
		input_type: inputTypes.ENTER.key,
		constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
		data: {},
		data_type: datatypes.STRING.key,
		sort_no: 1,
		group: "",
		placeholder: "",
		default_value: "",
		renderHeader: defRenderHeader,
		headerSx: { background: grey[300] },

		valueGetter: function (row, rawData = false) {
			if (rawData) return calculateNsvLost(row);
			return convertValueInCr(calculateNsvLost(row));
		},
		renderCell: function ({ row, field }) {
			let val = this.valueGetter(row);

			return (
				<StandardRenderCellWithChildren field={field} row={row} sx={renderCellStyle}>
					{/* <PercentBackgroundColor value={val}> */}
					{val}
					{/* </PercentBackgroundColor> */}
				</StandardRenderCellWithChildren>
			);
		},
		headerClassName: "default-header",
		unit: "INR CR.",
	},
};
